/**
 * @module qbLinkage/detectChanges/detectCategoryDelta
 * @description 轻量判断 QB 快照哪些字段类别发生变化（不跑完整 diff）
 */

import type { IQBSnapshot, TQBCategory } from '../types';
import { isCompareUserGroupActive } from '../handlers/columnSort/untils/compareUserGroup';
import { fieldListSignature, rawColumnListSignature, snapshotList } from '../fieldSnapshot';
import { detectCompareTime } from './detectCompareTime';

function isRawMode(snapshot: IQBSnapshot): boolean {
  return snapshot.mode === 'raw';
}

function resolveHasDateDimension(snapshot: IQBSnapshot): boolean {
  if (snapshot.hasDateDimension !== undefined) {
    return snapshot.hasDateDimension;
  }
  const dims = snapshotList(snapshot.columnDimensions, 'dimension');
  return dims.some(d => d.column_type === 'date');
}

export function isDimensionCategoryChanged(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): boolean {
  const prevDimSig = fieldListSignature(prev.columnDimensions, 'dimension');
  const nextDimSig = fieldListSignature(next.columnDimensions, 'dimension');
  return (
    prevDimSig !== nextDimSig ||
    resolveHasDateDimension(prev) !== resolveHasDateDimension(next)
  );
}

export function isMetricCategoryChanged(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): boolean {
  const prevMetSig = fieldListSignature(prev.columnMetrics, 'metric');
  const nextMetSig = fieldListSignature(next.columnMetrics, 'metric');
  return prevMetSig !== nextMetSig;
}

export function isCompareTimeCategoryChanged(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): boolean {
  const prevCompare = detectCompareTime(prev.columnFilters);
  const nextCompare = detectCompareTime(next.columnFilters);
  return (
    prevCompare.enabled !== nextCompare.enabled ||
    prevCompare.dateFieldKey !== nextCompare.dateFieldKey
  );
}

export function isUserFilterCategoryChanged(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): boolean {
  if (!!prev.hasUserFilters !== !!next.hasUserFilters) {
    return true;
  }
  if (isCompareUserGroupActive(prev) !== isCompareUserGroupActive(next)) {
    return true;
  }
  const prevLen = prev.columnUserFilters?.length ?? 0;
  const nextLen = next.columnUserFilters?.length ?? 0;
  return prevLen !== nextLen;
}

/** 明细模式：columnRawColumns 变化（聚合模式不参与） */
export function isRawColumnCategoryChanged(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): boolean {
  if (!isRawMode(prev) || !isRawMode(next)) {
    return false;
  }
  return (
    rawColumnListSignature(prev.columnRawColumns) !==
    rawColumnListSignature(next.columnRawColumns)
  );
}

export function isCategoryChanged(
  category: TQBCategory,
  prev: IQBSnapshot,
  next: IQBSnapshot,
): boolean {
  switch (category) {
    case 'dimension':
      return isDimensionCategoryChanged(prev, next);
    case 'metric':
      return isMetricCategoryChanged(prev, next);
    case 'compareTime':
      return isCompareTimeCategoryChanged(prev, next);
    case 'userFilter':
      return isUserFilterCategoryChanged(prev, next);
    case 'rawColumn':
      return isRawColumnCategoryChanged(prev, next);
    default:
      return false;
  }
}

export function detectChangedCategories(
  prev: IQBSnapshot,
  next: IQBSnapshot,
): Set<TQBCategory> {
  const categories = new Set<TQBCategory>();

  if (isDimensionCategoryChanged(prev, next)) {
    categories.add('dimension');
  }
  if (isMetricCategoryChanged(prev, next)) {
    categories.add('metric');
  }
  if (isCompareTimeCategoryChanged(prev, next)) {
    categories.add('compareTime');
  }
  if (isUserFilterCategoryChanged(prev, next)) {
    categories.add('userFilter');
  }
  if (isRawColumnCategoryChanged(prev, next)) {
    categories.add('rawColumn');
  }

  return categories;
}
